import { Component, OnChanges, inject, input } from '@angular/core';
import { cleanSubscriptionName } from '../../../models/Subscription';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ConstantsService } from '../../../services/utilities/constants.service';
import { Discount, DISCOUNT, Option, Subscription } from '@co-assist/library';
import { ImagesService } from 'app/services/utilities/images.service';

@Component({
    selector: 'app-shopping-cart',
    templateUrl: './shopping-cart.component.html',
    styleUrls: ['./shopping-cart.component.scss'],
    standalone: false
})
export class ShoppingCartComponent implements OnChanges {
    imgService = inject(ImagesService);
    private translate = inject(TranslateService);
    private toastr = inject(ToastrService);
    private constants = inject(ConstantsService);

    readonly selectedSubscription = input<Subscription>(undefined);
    readonly selectedOptions = input<Option[]>([]);
    readonly selectedDiscount = input<Discount>(undefined);
    displayedColumns: string[] = ['product', 'subtotal'];
    dataSource: Array<{ product: { productImg: string, optionImg: string, name: string, label?: string }, subtotal: number }> = [];
    shippingFee: number;

    ngOnChanges(): void {
        this.initDataSource();
    }

    /**
     * A subscription price to shopping cart
     */
    initDataSource(): void {
        this.dataSource = [];
        this.shippingFee = 0;
        this.addSubscriptionRow();
        this.addBuyingOptionRows();
        this.addRentingOptionRows();
        this.addRentingServiceOptionRows();
        this.addMonthlyRow();
        this.addAdditionalLines();
        if (this.selectedSubscription().name === 'saved' && this.selectedOptions().length === 0) {
            this.firstDebit();
        }
        if (this.shippingFee > 0) {
            this.addDeliveryFee();
        }
        if (this.selectedDiscount()) {
            this.addDiscountRow();
        }
        this.addTotal();
    }

    private addSubscriptionRow() {
        const labelTranslations = this.translate.instant(['REGISTRATION.STEP.PAYMENT.WATCH', `REGISTRATION.STEP.PAYMENT.SUBSCRIPTION.${cleanSubscriptionName(this.selectedSubscription().name)}`]);
        const subscriptionLabel = Object.values(labelTranslations).filter(v => v !== '').join(' ');
        const productLine = {
            product: {
                productImg: '/assets/images/watchDisplay/watchCart.png',
                optionImg: null,
                name: `subscription_${this.selectedSubscription().name}`,
                label: subscriptionLabel
            },
            subtotal: this.selectedSubscription().watchPrice
        };

        // include renting options in product label
        this.selectedOptions().filter(opt => opt.type === 'renting' && opt.equipment !== 'service').forEach(option => {
            productLine.product.label += ` + ${this.translate.instant(`REGISTRATION.STEP.PAYMENT.${option.equipment.toUpperCase()}`)}`;
            productLine.product.optionImg = this.imgService.getEquipment(option.equipment);
        });

        this.dataSource.push(productLine);
    }

    private addBuyingOptionRows() {
        this.selectedOptions().filter(opt => opt.type === 'purchase').forEach(option => {
            this.shippingFee += option.shippingPrice;
            this.dataSource.push({
                product: {
                    productImg: option.name !== 'FEE' ? this.imgService.getEquipment(option.equipment) : null,
                    optionImg: null,
                    name: 'buy_' + option.equipment,
                    label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.${option.equipment.toUpperCase()}`)
                },
                subtotal: option.unitPrice
            });
        });
    }

    private addRentingOptionRows() {
        this.selectedOptions().filter(opt => opt.type === 'renting' && opt.equipment !== 'service').forEach(option => {
            this.shippingFee += option.shippingPrice;
            this.dataSource.push({
                product: {
                    productImg: null,
                    optionImg: null,
                    name: 'rent_' + option.equipment,
                    label: this.translate.instant('REGISTRATION.STEP.PAYMENT.RENTING') + ' ' + this.translate.instant(`REGISTRATION.STEP.PAYMENT.${option.equipment.toUpperCase()}`)
                },
                subtotal: option.unitPrice
            });
        });
    }

    private addRentingServiceOptionRows() {
        this.selectedOptions().filter(opt => opt.type === 'renting' && opt.equipment === 'service').forEach(option => {
            this.shippingFee += option.shippingPrice;
            this.dataSource.push({
                product: {
                    productImg: null,
                    optionImg: null,
                    name: 'rent_' + option.equipment,
                    label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.${option.name.toUpperCase()}`)
                },
                subtotal: option.unitPrice
            });
        });
    }

    private addMonthlyRow() {
        const selectedSubscription = this.selectedSubscription();
        if (selectedSubscription && selectedSubscription.firstDebitingOffset === 0) {
            this.dataSource.push({
                product: {
                    productImg: null,
                    optionImg: null,
                    name: 'monthly',
                    label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.SUBSCRIPTION.MONTHLY`)
                },
                subtotal: selectedSubscription.servicePrice
            });
        }
    }

    /**
     * Add some extras lines in order to explain to the customer its futures debit
     */
    private addAdditionalLines(): void {
        const additionals: Array<any> = [
            {
                name: 'economy', label: this.translate.instant('REGISTRATION.STEP.PAYMENT.MENSUAL_FEES',
                    { date: this.selectedSubscription().getFirstDebittingDay() }), value: this.constants.subscriptionList.find(sub => sub.name === 'economy').servicePrice
            },
            {
                name: 'economy2', label: this.translate.instant('REGISTRATION.STEP.PAYMENT.MENSUAL_FEES',
                    { date: this.selectedSubscription().getFirstDebittingDay() }), value: this.constants.subscriptionList.find(sub => sub.name === 'economy2').servicePrice
            },
            {
                name: 'economy3', label: this.translate.instant('REGISTRATION.STEP.PAYMENT.MENSUAL_FEES',
                    { date: this.selectedSubscription().getFirstDebittingDay() }), value: this.constants.subscriptionList.find(sub => sub.name === 'economy3').servicePrice
            },
            {
                name: 'saved', label: this.translate.instant('REGISTRATION.STEP.PAYMENT.MENSUAL_FEES',
                    { date: this.selectedSubscription().getFirstDebittingDay() }), value: this.constants.subscriptionList.find(sub => sub.name === 'saved').servicePrice
            }];
        const selected = additionals.find(elem => (elem.name === this.selectedSubscription().name));
        if (selected) {
            this.dataSource.push({
                product: {
                    productImg: null,
                    optionImg: null,
                    name: 'additional',
                    label: selected.label,
                },
                subtotal: selected.value
            });
        }
    }

    /**
    * A first debit payment must be process ? - in the mandatory tokenization case
    * So we make a 1ct payment
    */
    private firstDebit(): void {
        this.dataSource.push({
            product: {
                productImg: null,
                optionImg: null,
                name: 'first_debit',
                label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.FIRST_DEBIT`)
            },
            subtotal: 0.01
        });
    }

    /**
     * Add to the shopping cart information about delivery fees
     */
    private addDeliveryFee(): void {
        this.dataSource.push({
            product: {
                productImg: null,
                optionImg: null,
                name: 'delivery',
                label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.DELIVERY`)
            },
            subtotal: this.shippingFee
        });
    }

    private addDiscountRow() {
        let discountValue = 0;
        const selectedSubscription = this.selectedSubscription();
        if (selectedSubscription && selectedSubscription.firstDebitingOffset === 0) {
            discountValue = selectedSubscription.servicePrice - selectedSubscription.getServicePrice(this.selectedDiscount());
        }
        if (selectedSubscription && selectedSubscription.watchPrice > 0) {
            discountValue += selectedSubscription.watchPrice - selectedSubscription.getWatchPrice(this.selectedDiscount());
        }
        const selectedOptions = this.selectedOptions();
        if (selectedOptions && selectedOptions.length > 0) {
            const optionsPrice: number = Option.getRentingTotal(selectedOptions) + Option.getPurchasingTotal(selectedOptions);
            discountValue += optionsPrice - this.selectedDiscount().applyDiscount(optionsPrice, DISCOUNT.Applicability.OPTION);

        }
        if (this.shippingFee > 0) {
            discountValue += this.shippingFee - this.selectedDiscount().applyDiscount(this.shippingFee, DISCOUNT.Applicability.SHIPPING);
        }
        if (discountValue === 0) {
            this.toastr.error(this.translate.instant('DISCOUNT_CODE.TOAST.INVALID_CODE'));
        } else {
            this.toastr.success(this.translate.instant('DISCOUNT_CODE.TOAST.VALID_CODE'));
            this.dataSource.push({
                product: {
                    productImg: null,
                    optionImg: null,
                    name: 'discount',
                    label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.DISCOUNT.LABEL`)
                },
                subtotal: -discountValue
            });
        }
    }

    /**
     * Add the total amount to pay to the shopping cart
     */
    private addTotal(): void {
        this.dataSource.push({
            product: {
                productImg: null,
                optionImg: null,
                name: 'total',
                label: this.translate.instant(`REGISTRATION.STEP.PAYMENT.TOTAL`)
            },
            subtotal: this._getTotalCost() === 0 ? 0.01 : this._getTotalCost()
        });
    }

    private _getTotalCost(): number {
        return this.dataSource
            .filter(elem => elem.product.name !== 'additional')
            .map(t => t.subtotal).reduce((acc, value) => acc + value, 0);
    }
}
